import {Component,Inject,Input,OnInit,ViewChild} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {TypePortee} from "@domain/workflow/workflow";
import {Localisation} from "@domain/geolocalisation/localisation";
import {TypeAdresse,TypeAdresseEnum,TypeAdresseRef} from "@domain/geolocalisation/type-adresse";
import {forkJoin,of} from "rxjs";
import {GeolocalisationTool} from "@domain/geolocalisation/geolocalisationTool";
import {MatLegacyTabGroup as MatTabGroup} from "@angular/material/legacy-tabs";
import {TypeGeolocalisation} from "@domain/geolocalisation/typeGeolocalisation";
import {Lieu} from "@domain/lieu/lieu";
import {LieuService} from "../lieu.service";
import {LieuLocalisationComponent} from "./localisation/lieu-localisation.component";
import {Etablissement} from "@domain/lieu/etablissement";
import {Adresse} from "@domain/profil/adresse";
import {LieuParam} from "@domain/common/lieu/lieu-param";
import {filter} from "rxjs/operators";
import {LieuGeolocalisationService} from "../lieu-geolocalisation.service";

@Component({
    templateUrl: './lieu-selection.component.html',
    host: {'data-test-id': 'lieu-selection'},
})
export class LieuSelectionComponent implements OnInit {
    /** Définition du type pour le template */
    readonly TypePortee = TypePortee;

    /** Index de l'onglet à afficher suivant le type de lieu */
    @Input() tabIndex: number = 0;

    /** Composant de saisie d'une adresse */
    @ViewChild(LieuLocalisationComponent)
    lieuLocalisationComponent: LieuLocalisationComponent;

    /** Le groupe d'onglets */
    @ViewChild("matTabGroup")
    matTabGroup: MatTabGroup;

    /** Type de l'adresse */
    typeAdresse: TypeAdresse;

    /** Liste des adresses personnelles / professionelles */
    listeAdresse:Array<Adresse>;

    /** Liste des établissements */
    listeEtablissement: Array<Etablissement>;

    /** Objet utilisé pour la saisie, qui sera initialisé avec les valeurs du lieu passé en paramètre de la popup */
    localisationSaisie: Localisation = {};

    /** Configuration de la géolocalisation, désactivée par défaut */
    geolocalisationTool: GeolocalisationTool = {
        geolocalisationTool: TypeGeolocalisation.DISABLED
    };

    /** Paramètre de configuration indiquant si la saisie d'une adresse est autorisée */
    isSaisieAutorisee: boolean = false;

    /**
     * Constructeur
     */
    constructor(private lieuService: LieuService,
                private lieuGeolocService: LieuGeolocalisationService,
                private matDialogRef: MatDialogRef<any,any>,
                @Inject(MAT_DIALOG_DATA) public data: {
                    isSaisieAutorisee: boolean,
                    typeLieu: number,
                    lieu: Lieu,
                    lieuParam: LieuParam
                }) {

        //Vérification de l'autorisation de saisie d'une adresse depuis la configuration du type entité
        this.isSaisieAutorisee = this.data.isSaisieAutorisee;

        //Récupération du type d'adresse correspondant au type de lieu
        this.typeAdresse = TypeAdresseRef.values.find(adr => adr.value === this.data.typeLieu);

        //Index de l'onglet à afficher suivant le type d'adresse déterminé
        this.tabIndex = this.typeAdresse?.tabIndex;

        //Décalage de l'index si l'onglet "Lieux récents" est affiché (ajouté en première position)
        if (this.data.lieuParam.isShowLieuxRecents && !!this.typeAdresse && this.typeAdresse?.type !== TypeAdresseEnum.PERSONNELLE) {
            this.tabIndex = (this.tabIndex ?? 0) + 1;
        }

        //Construction d'un objet de géolocalisation si le lieu provient d'une saisie
        if (this.typeAdresse?.type === TypeAdresseEnum.AUTRE) {
            //Initialisation de l'objet
            this.localisationSaisie = {
                adresse: this.data.lieu?.adresse,
                rue: this.data.lieu?.rue,
                codePostal: this.data.lieu?.codePostal,
                ville: this.data.lieu?.ville,
                pays: this.data.lieu?.pays,
                codePays: this.data.lieu?.codePays
            }
        }

    }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Utilisation d'un forkjoin pour pouvoir attendre l'exécution de tous les appels http
        forkJoin([
            this.lieuGeolocService.getGeolocalisationTool(),
            this.data.lieuParam.isShowAdresseBaseAndEtablissement ? this.lieuService.loadAdresses(this.data.lieuParam) : of(null)
        ]).subscribe({
            next: ([geolocalisationTool,adresses]) => {
                //Récupération de la configuration de la géolocalisation et surcharge de la conf initialisée par défaut
                this.geolocalisationTool = {
                    ...this.geolocalisationTool,
                    ...geolocalisationTool
                };

                //Récupération de la liste des adresses et des établissements
                //Utilisé que pour les adresses simples et l'affichage des établissements
                if (adresses) {
                    //Si l'adresse est contenu dans un sous élement, le remonter
                    this.listeAdresse = adresses.listeAdresses.map((adresse: any) => {
                        if (adresse.adresse) {
                            return {
                                ...adresse,
                                rue: adresse.adresse?.rue,
                                ville: adresse.adresse.ville,
                                codePostal: adresse.adresse.codePostal,
                                pays: adresse.adresse.pays
                            }
                        }
                        return adresse;
                    });

                    //Si l'adresse est contenu dans un sous élement, le remonter
                    this.listeEtablissement = adresses.listeEtablissements?.map(etablissement => {
                        if (etablissement.adresse) {
                            return {
                                ...etablissement,
                                rue: etablissement.adresse.rue,
                                ville: etablissement.adresse.ville,
                                codePostal: etablissement.adresse.codePostal,
                                pays: etablissement.adresse.pays
                            }
                        }
                        return etablissement;
                    }) ?? [];
                }
            }
        });
    }

    /**
     * Retourne True si l'onglet affiché est celui de la saisie, False sinon.
     */
    setTabSaisieActif() {
        //Décalage de l'index si l'onglet "Lieux récents" est affiché
        const offset = this.data.lieuParam.isShowLieuxRecents && !!this.typeAdresse && this.typeAdresse?.type !== TypeAdresseEnum.PERSONNELLE ? 1 : 0;
        this.tabIndex = TypeAdresseRef.get(TypeAdresseEnum.AUTRE).tabIndex + offset;
    }

    /**
     * Sélection d'une adresse personnelle/professionnelle
     *
     * @param adresse Adresse personnelle/professionnelle (source : onglet mes adresses)
     */
    selectAdresse(adresse: Adresse): void {
        //Si l'option est activée et que l'adresse n'est pas géolocalisée, on ouvre l'outil de géolocalisation
        if (this.data.lieuParam.isGeolocaliserOnSelect && !adresse.isGeolocalisee) {
            //Ouvre l'outil de géolocalisation
            this.lieuGeolocService.openGeolocalisationTool(
                this.geolocalisationTool,
                null,
                adresse,
                this.data.lieuParam
            )
                .pipe(filter(data => data != null))
                .subscribe(data => {
                    //Utilisation directe de la localisation géolocalisée pour le frais d'IK
                    if (data?.geolocalisation) {
                        this.selectAutre(data.geolocalisation);
                    }
                });
        } else if (this.data.lieuParam.isOngletSaisieWithAdresseIncomplete && this.lieuService.isAdresseIncomplete(adresse)) {
            //Sur l'onglet mes adresses avec filtres, dans le cas d'une adresse incomplete, on passe à l'onglet de saisie pour la compléter
            this.setTabSaisieActif();
            this.lieuLocalisationComponent.initFromAdresse(adresse);
        } else {
            //Fermeture de l'écran
            this.closeDialog(adresse);
        }
    }

    /**
     * Ferme la popup en retournant les données de l'adresse sélectionnée
     *
     * @param adresse L'adresse sélectionnée
     */
    private closeDialog(adresse: Adresse) {
        //Fermeture de l'écran
        this.matDialogRef.close({
            adresse: this.lieuService.formatAdressetoString(adresse),
            objet: adresse,
            type: adresse.type ? TypeAdresseRef.find(adresse.type).value : null
        });
    }

    /**
     * Sélection d'un établissement
     *
     * @param etablissement Etablissement (source : onglet Etablissements)
     */
    selectEtablissement(etablissement: Etablissement): void {
        //Fermeture de l'écran
        this.matDialogRef.close({
            adresse: this.lieuService.formatAdressetoString(etablissement as Adresse),
            objet: etablissement,
            type: TypeAdresseRef.get(TypeAdresseEnum.ETABLISSEMENT).value
        });
    }

    /**
     * Sélection d'une adresse saisie manuellement ou géolocalisée.
     *
     * @param localisation  Objet représentant la localisation de l'adresse.
     */
    selectAutre(localisation?: Localisation): void {
        if (!localisation) {
            this.matDialogRef.close();
        } else {
            //Sélection de l'adresse à la fermeture de la popin
            this.matDialogRef.close({
                adresse: this.lieuService.getAdresseFromLocalisation(localisation),
                objet: localisation,
                type: TypeAdresseRef.get(TypeAdresseEnum.AUTRE).value
            });
        }
    }

}
